import React, { useState, useRef, useMemo, useEffect } from "react";
import { IconButton, TextField, useTheme, inputBaseClasses, outlinedInputClasses, svgIconClasses } from "@mui/material";
import styled from "@emotion/styled";
import SearchIcon from "src/icons/SearchIcon";
import CloseIcon from "src/icons/CloseIcon";
import Tooltip from "@hyro-components/Tooltip";
import { useIsMobile } from "@hooks";
import debounce from "lodash/debounce";
import { HyroIconButton } from "@hyro-components";
import { ICON_BUTTON_VARIANTS } from "@hyro-components/hyro-icon-button/HyroIconButton.consts";

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "customWidth" && prop !== "customHeight"
})(({ open, customWidth, customHeight = 36, theme, disabled }) => ({
  height: `${customHeight}px`,
  width: customWidth ? customWidth : (open ? "200px" : "36px"),
  transition: "width 0.3s ease-in-out",
  [`& .${outlinedInputClasses.root}`]: {
    maxHeight: `${customHeight}px`,
    paddingInline: "8px",
    cursor: open ? "text" : "pointer",
    backgroundColor: "white",
    gap: "8px",
    ":hover": open ? null : {
      backgroundColor: "#EBEBFD",
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: disabled ? theme.palette.system[300] : theme.palette.secondary[500]
      }
    }
  },
  [`& .${inputBaseClasses.input}`]: { paddingBlock: `${(customHeight - 23) / 2}px` },
  [`& .${svgIconClasses.root}`]: { width: "20px" },
  [`& .${inputBaseClasses.root} .${outlinedInputClasses.notchedOutline}`]: {
    border: `1px solid ${disabled ? theme.palette.system[300] : theme.palette.system[400]}`
  },
  [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
    borderColor: disabled ? theme.palette.system[200] : theme.palette.system[400]
  },
  [`& .${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]: {
    border: `1px solid ${disabled ? theme.palette.system[300] : theme.palette.primary[500]}`
  }
}));

const SearchBar = ({ onChange, stayOpen, customWidth, customHeight, placeholder = "Search", hideTooltip, shouldAutoFocus, handleKeyDown, testId, disabled, initialValue }) => {
  const [open, setOpen] = useState(initialValue);
  const [value, setValue] = useState(initialValue || "");
  const [hover, setHover] = useState(false);
  const searchRef = useRef();
  const theme = useTheme();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
      setOpen(true);
    }
  }, [initialValue]);

  const debouncedOnChange = useMemo(
    () => debounce((value) => onChange(value), 300),
    [onChange]
  );

  useEffect(() => () => {
    debouncedOnChange.cancel();
  }, [debouncedOnChange]);

  const handleSearchIconClick = (e) => {
    e.stopPropagation();
    setOpen(true);
    searchRef.current?.focus();
  };

  const handleSearchBlur = () => {
    if (!value) {
      setOpen(false);
    }
  };

  const handleSearchTextChange = (e) => {
    e.stopPropagation();
    const { target: { value } } = e;
    setValue(value);
    debouncedOnChange(value);
  };

  const handleClearSearch = (e) => {
    e.stopPropagation();
    setValue("");
    debouncedOnChange("");
    searchRef.current?.focus();
  };

  return (
    <Tooltip title={hideTooltip ? "" : "Search"} open={hover && !open && !isMobile}>
      <StyledTextField
        sx={{ pointerEvents: disabled ? "none" : "auto" }}
        data-testid={`${testId ? `${testId}-` : ""}search-bar`}
        disabled={disabled}
        open={stayOpen || open}
        autoFocus={shouldAutoFocus}
        customWidth={customWidth}
        customHeight={customHeight}
        onBlur={handleSearchBlur}
        onChange={handleSearchTextChange}
        onClick={handleSearchIconClick}
        onFocus={handleSearchIconClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onKeyDown={(e) => {
          if (e.key !== "Escape") {
            // Prevents auto-selecting item while typing (default Select behavior)
            e.stopPropagation();
          }

          // handle up and down keys
          if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "Enter") {
            handleKeyDown?.(e);
          }
        }}
        inputRef={searchRef}
        value={value}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <SearchIcon stroke={disabled ? theme.palette.system[400] : hover && (!open && !stayOpen) ? theme.palette.primary.main : theme.palette.system[700]} />
          ),
          endAdornment: value ? (
            <HyroIconButton disabled={disabled} variant={ICON_BUTTON_VARIANTS.CLEAR} onClick={handleClearSearch} data-testid={`${testId ? `${testId}-clear` : ""}`}>
              <CloseIcon color={theme.palette.text[800]} />
            </HyroIconButton>
          ) : null
        }}
      />
    </Tooltip>
  );
};

export default SearchBar;
